///<reference path="global.d.ts"/>

import React from "react";
import * as Sentry from "@sentry/react";

import { applyMiddleware, compose } from "redux";
import { v4 as uuid } from "uuid";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import { CoreContext, ChatStreamContext } from "@planzone/view-models";
import axios from "axios";
import dynamicMiddlewares from "redux-dynamic-middlewares-2";
import "./translations";
import "./styles.scss";

import { createCore, UseCases } from "@planzone/core";
import {
  createService,
  createDateOps,
  createStreamChatJob,
} from "@planzone/services";
import { createEventDispatcher, events, Event } from "@planzone/events";
import { createSelector, createState } from "@planzone/state";
import { createSession } from "./components/session";
import { createGateKeeper } from "./components/gatekeeper";
import { createPreferencesRepository } from "./components/preferencesRepository";
import { createTokenProvider } from "./components/tokenProvider";
import { createHttpClient } from "./components/httpClient";
import { createTokenDecoder } from "./components/tokenDecoder";
import { decoratePresenter } from "./components/presenterDecorator";
import { createSearchEngine } from "./components/searchEngine";
import { createTokenRefresher } from "./components/tokenRefresher";
import { createProjectHistory } from "./components/projectHistory";
import { createApiUrlProvider } from "./components/apiUrlProvider";
import { createUaaUrlProvider } from "./components/uaaUrlProvider";
import { createDocumentUrlProvider } from "./components/documentUrlProvider";
import { createChatbotUrlProvider } from "./components/chatbotUrlProvider";
import { createFileSystem } from "./components/fileSystem";
import { createIconExtractor } from "./components/iconExtractor";
import { createCache } from "./components/httpCache";
import { createDocumentsHttpClient } from "./components/documentsHttpClient";
import { createExportGenerator } from "./components/exportGenerator";
import { detectBrowserDateFormat } from "@common/detectBrowserDateFormat";
import { createTimeZoneProvider } from "./components/timeZoneProvider";
import { Cache } from "./components/cache";
import { createWSClient } from "./components/wsClient";
import { createWsPublisherDecorator } from "./components/wsPublisherDecorator";
import { RequestInterceptor } from "./components/requestInterceptor";
import { App } from "./app";
import reportWebVitals from "./reportWebVitals";

const isProduction = process.env.NODE_ENV === "production";
const wsUrl: string = process.env.MESSAGING_URL || "";
let wsClientId: string = "";

axios.interceptors.request.use((config) => ({
  ...config,
  url: config.url ? config.url.replace("{?projection}", "") : config.url,
}));

const sessionId = uuid();
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
});

Sentry.setTag("sessionId", sessionId);

document.addEventListener(
  "wheel",
  (event) => {
    if (
      document.activeElement instanceof HTMLInputElement &&
      document.activeElement.type === "number"
    ) {
      document.activeElement.blur();
      event.preventDefault();
    }
  },
  { passive: false },
);

const documentsUrl = process.env.DOCUMENTS_URL;

function main() {
  const wsClient = createWSClient(wsUrl);
  const state = !isProduction
    ? createState(
        compose(
          applyMiddleware(
            // () => (next) => (action) => {
            //   console.groupCollapsed(action.type);
            //   console.log(action.data);
            //   console.groupEnd();
            //   next(action);
            // },
            () => (next) => (action) => {
              if (action.type === events.retrieveCurrentPlanzoneSucceeded) {
                wsClient.connect();
                wsClient.subscribe(action.data.uuid);
              }

              next(action);
            },
            dynamicMiddlewares,
          ),
          sentryReduxEnhancer,
        ),
      )
    : createState(
        compose(
          applyMiddleware(
            () => (next) => (action) => {
              if (action.type === events.authenticateSucceeded) {
                Sentry.setTag("userEmail", action.data.email);
              }

              if (action.type === events.retrieveCurrentPlanzoneSucceeded) {
                Sentry.setTag("planzoneId", action.data.uuid);
                Sentry.setTag("planzoneName", action.data.name);
                wsClient.connect();
                wsClient.subscribe(action.data.uuid);
              }

              Sentry.addBreadcrumb({
                category: action.type,
                level: "info",
              });

              action.type.substring(
                action.type.length,
                action.type.length - "Failed".length,
              ) === "Failed" &&
                Sentry.captureMessage(action.type, {
                  level: "error",
                  extra: action.data,
                });

              next(action);
            },
            dynamicMiddlewares,
          ),
          sentryReduxEnhancer,
        ),
      );
  const selector = createSelector(() => state.getState());
  const session = createSession(() => selector.getCurrentPlanzone());
  const eventDispatcher = createWsPublisherDecorator(
    createEventDispatcher({
      sendEvent: state.dispatch,
    }),
    (event) => {
      session
        .retrieve()
        .then(({ accessToken }) => accessToken)
        .then((accessToken) =>
          fetch("/ws", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
              planzone: selector.getCurrentPlanzone().uuid,
              "x-client-id": wsClientId,
            },
            body: JSON.stringify({
              type: event.type,
              data: {
                ...(event.data as object),
                _sender: selector.getAccount().resource.uuid,
              },
            }),
          }),
        )
        .catch(() => {});
    },
  );
  const httpClient = createHttpClient();
  const wrappedHttpClient = createCache(
    createTokenRefresher(session)(createTokenProvider(session)(httpClient)),
  );
  const apiHttpClient = createTimeZoneProvider(
    createApiUrlProvider(wrappedHttpClient),
  );
  const repository = createService(
    apiHttpClient,
    createUaaUrlProvider(wrappedHttpClient),
    createDocumentUrlProvider(wrappedHttpClient),
    createChatbotUrlProvider(wrappedHttpClient),
  );
  const chatStream = createStreamChatJob(
    () => session.retrieve().then(({ accessToken }) => accessToken),
    "/assistant-api",
  );
  const docAxios = axios.create();

  docAxios.interceptors.request.use((config) => ({
    ...config,
    url: config.url
      ? config.url.replace(`${documentsUrl}`, "/docs")
      : config.url,
  }));

  const core = decoratePresenter(eventDispatcher)(
    createCore({
      gatekeeper: createGateKeeper(() => selector.getCurrentPlanzone()),
      session,
      tokenDecoder: createTokenDecoder(wrappedHttpClient),
      repository: repository,
      dateOps: createDateOps(),
      workspace: repository,
      projectHistory: createProjectHistory(
        () => selector.getAccount().resource,
      ),
      fileSystem: createFileSystem(session, docAxios),
      httpClient: createDocumentsHttpClient(session, docAxios),
      preferencesRepository: createPreferencesRepository(),
      iconExtractor: createIconExtractor(),
      duplicateInspector: repository,
      inviters: repository,
      searchEngine: createSearchEngine(apiHttpClient, selector),
      activityReport: repository,
      exportGenerator: createExportGenerator(apiHttpClient, () => {
        const prefs = selector.getUserPreferences();
        return prefs["reportsDateFormat"] || detectBrowserDateFormat();
      }),
    }),
  );

  core.retrievePreferences();

  wsClient.onMessage((message) => {
    if (
      !!message &&
      typeof message === "object" &&
      "type" in message &&
      message.type === "connected" &&
      "clientId" in message
    ) {
      wsClientId = message.clientId as string;
    } else if (
      !!message &&
      typeof message === "object" &&
      "event" in message &&
      "data" in message
    ) {
      state.dispatch({
        type: message.event as Event["type"],
        data: message.data as Event["data"],
      } as Event);
    }
  });

  createRoot(document.getElementById("root")!).render(
    <HelmetProvider>
      {/* @ts-expect-error react-redux types incompatible with React 19 */}
      <Provider store={state}>
        <CoreContext.Provider
          value={{
            core: core,
            selector: selector,
            eventDispatcher,
          }}
        >
          <ChatStreamContext.Provider value={chatStream}>
            <Cache>
              <RequestInterceptor>
                <App />
              </RequestInterceptor>
            </Cache>
          </ChatStreamContext.Provider>
        </CoreContext.Provider>
      </Provider>
    </HelmetProvider>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
main();
